import type { ReactNode } from 'react';
import React, { useCallback, useMemo } from 'react';

import { HooksDisplayGrid } from 'ui-components/components/hooks-display-grid';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import { useEnhancedReturnsContext } from '../grids/enhanced-returns-context';
import { SSRFilterBar } from './ssr-filter-bar';
import { NoSearchResultsGridPlaceholder, NoFilterResultsGridPlaceholder } from '../grids/placeholders';
import { PagingControl } from '../../../../components/paging-control';
import { emptyArray } from 'ui-components/default-props/empty';
import { STORE_COLUMN_ID, REQUEST_NUMBER_COLUMN_ID } from '../grids/columns';

type GridColumn = {
  id: string;
  [key: string]: unknown;
};

type SSRBaseGridProps = {
  columns?: readonly GridColumn[];
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  viewSpecificFilters?: readonly any[];
  noResultsPlaceholder?: ReactNode;
};

type PlaceholderProps = {
  isLoading: boolean;
  hasReturns: boolean;
  isSearchMode: boolean;
  hasFilters: boolean;
  noResultsPlaceholder: ReactNode;
};

const mapConfiguration = (columns: GridColumn[], columnsConfiguration: GridColumn[]): GridColumn[] => {
  const columnMap = columns.reduce<Record<string, GridColumn>>((acc, column) => {
    const { id } = column;
    return {
      ...acc,
      [id]: column,
    };
  }, {});

  return columnsConfiguration.reduce<GridColumn[]>((acc, config) => {
    const column = columnMap[config.id];

    if (column) {
      acc.push({
        ...config,
        ...column,
      });
    }

    return acc;
  }, []);
};

const Placeholder = ({ isLoading, hasReturns, isSearchMode, hasFilters, noResultsPlaceholder }: PlaceholderProps) => {
  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (hasReturns) {
    return null;
  }

  if (hasFilters) {
    return <NoFilterResultsGridPlaceholder />;
  }

  if (isSearchMode) {
    return <NoSearchResultsGridPlaceholder />;
  }

  // Return custom placeholder passed as prop
  return noResultsPlaceholder || null;
};

export const SSRBaseGrid = ({ columns = emptyArray, viewSpecificFilters = emptyArray, noResultsPlaceholder = null }: SSRBaseGridProps) => {
  const {
    returns,
    isLoading,
    totalRecords,
    page,
    pageSize,
    updatePageInformation,
    totalPages,
    viewStartingCount,
    viewEndingCount,
    columnsConfig,
    updateColumnsConfig,
    isSearchMode,
    filters,
  } = useEnhancedReturnsContext();
  const [selectedCount, setSelectedCount] = useAsyncSafeState(0);

  const columnsConfigWithStore = useMemo(() => {
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    if (columnsConfig.some((c: any) => c.id === STORE_COLUMN_ID)) {
      return columnsConfig;
    }
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    const requestIndex = columnsConfig.findIndex((c: any) => c.id === REQUEST_NUMBER_COLUMN_ID);
    const insertAt = requestIndex >= 0 ? requestIndex + 1 : columnsConfig.length;
    const storeEntry = { id: STORE_COLUMN_ID, minWidth: 200, width: 200, nonsortable: true, index: insertAt };
    return [...columnsConfig.slice(0, insertAt), storeEntry, ...columnsConfig.slice(insertAt)];
  }, [columnsConfig]);

  const hasFilters = !!filters && Object.keys(filters).length > 0;

  const onPageChange = useCallback(
    (newPage: number, newPageSize: number) => {
      updatePageInformation(newPage, newPageSize);
    },
    [updatePageInformation],
  );

  const onSelectedChanged = useCallback(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    (selected: any[]) => {
      setSelectedCount(selected.length);
    },
    [setSelectedCount],
  );

  const onColumnsChanged = useCallback(
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    (config: any) => {
      updateColumnsConfig(config);
    },
    [updateColumnsConfig],
  );

  // TODO: Once all merchants have been migrated to the SSR grid, remove the old
  // enhanced-returns grid (enhanced-returns/grids/) and its supporting context/components.
  // Tracked in: https://auctane.atlassian.net/browse/RETURN-103
  return (
    <>
      <SSRFilterBar viewSpecificFilters={viewSpecificFilters} />
      <HooksDisplayGrid
        // @ts-expect-error type not defined
        // oxlint-disable-next-line @typescript-eslint/no-explicit-any
        columns={mapConfiguration(columns, columnsConfigWithStore) as any}
        data={isLoading ? emptyArray : returns}
        getColumnId={(column: GridColumn) => column.id}
        onColumnsChanged={onColumnsChanged}
        onOrderByChanged={onColumnsChanged}
        onSelectedChanged={onSelectedChanged}
        placeholder={
          <Placeholder
            hasFilters={hasFilters}
            hasReturns={returns.length > 0}
            isLoading={isLoading}
            isSearchMode={isSearchMode}
            noResultsPlaceholder={noResultsPlaceholder}
          />
        }
        renderCheckbox={false}
      />
      <PagingControl
        numberSelected={selectedCount}
        onPageChange={onPageChange}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        totalRecords={totalRecords}
        viewEndingCount={viewEndingCount}
        viewStartingCount={viewStartingCount}
      />
    </>
  );
};
